/**
 * Executive left pane — always visible, including on the brand picker
 * (2026-08-16 UI review): gray-toned to break the screen up visually
 * (RD screenshots), navigation pinned to the TOP so the variable-length
 * scenario list can never crowd it out.
 *
 * Nav (RD mapping): "Brand Portfolio" returns to the picker and is
 * always enabled (it doubles as "change brand" — no separate button);
 * "Query Forecast" and "What-If Analysis" are workspace destinations,
 * both disabled until a brand is selected. What-If Analysis is further
 * gated on `whatIfEnabled` (design doc EXEC_WHATIF_v1_design.md D3: both
 * curated sets — outputs and inputs — must be non-empty; the parent
 * treats a failed options fetch the same as empty). Reporting lands here
 * later as its surface ships.
 *
 * Scenario rules (design doc §5): all AI-enabled (tagged) scenarios of
 * the brand's instance, first pre-checked upstream, minimum one selected
 * (unchecking the last is a no-op), maximum MAX_SCENARIOS (blocked rows
 * disable at the cap). Rendered as one full-width chip per line —
 * deliberately smaller type than the Duplo scale; the selected tint is
 * the app theme's secondary indigo at low alpha.
 *
 * Single-select mode (`scenarioMode: 'single'`, EXEC_WHATIF_v1_design.md
 * D1): the pane *displays* radio semantics — clicking a chip replaces
 * the selection, no cap/min tooltips, and only the first id in
 * `selectedScenarioIds` renders checked. The parent (index.tsx) is the
 * source of truth: it never mutates its stored multi-selection when
 * entering What-If, it only stops passing further ids to display here.
 * `onToggleScenario` still receives a single scenario id in both modes;
 * index.tsx branches on its own `destination` state to replace
 * (`setScenarioIds([id])`) rather than toggle when handling a click
 * made while on What-If.
 */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
// The app remaps MUI's class prefix (Mui → Trinity), so hardcoded
// ".MuiChip-*" selectors match nothing; chipClasses tracks the real names.
import { chipClasses } from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AlignHorizontalLeftOutlinedIcon from '@mui/icons-material/AlignHorizontalLeftOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';

import {
  ExecutiveBrand,
  ExecutiveDestination,
  MAX_SCENARIOS,
  TornadoPair,
} from './types';

// 'single' is What-If Analysis (D1: one scenario per run); 'multi' is
// Query Forecast's existing behavior, preserved exactly; 'pair' is the
// tornado destination's ordered Baseline / Compare-to selection.
export type ScenarioSelectMode = 'multi' | 'single' | 'pair';

interface SidebarProps {
  // null on the brand picker — the pane still renders, with Query
  // Forecast and What-If Analysis disabled and no scenario section.
  brand: ExecutiveBrand | null;
  selectedScenarioIds: number[];
  onToggleScenario: (scenarioId: number) => void;
  onBrandPortfolio: () => void;
  // Nav state machine (index.tsx).
  destination: ExecutiveDestination;
  onNavigate: (destination: ExecutiveDestination) => void;
  // D3: both curated sets (outputs, inputs) non-empty for this brand.
  whatIfEnabled: boolean;
  // ≥1 enabled tornado prompt AND ≥2 tagged scenarios (a pair needs two).
  tornadoEnabled: boolean;
  scenarioMode: ScenarioSelectMode;
  // Pair mode only: the draft pair bound to the two dropdowns. The parent
  // owns draft-vs-committed; changing a dropdown never re-runs anything —
  // the Update button (armed when draft ≠ last-run pair on an open
  // answer) is the only trigger, because a tornado run is a slow async
  // calculation, unlike QF's cheap scenario-toggle re-run.
  tornadoPair?: TornadoPair | null;
  onTornadoPairChange?: (pair: TornadoPair) => void;
  tornadoUpdateArmed?: boolean;
  onTornadoUpdate?: () => void;
}

export const EXEC_SIDEBAR_WIDTH = 300;

// The app theme puts a 5px solid left border on selected ListItemButtons
// (theme.ts MuiListItemButton override) with no padding compensation, so
// selection shifts the label 5px right. Reserve the same 5px on the
// unselected state so nav labels stay left-aligned regardless of which
// item is active. (State classes like .Mui-selected keep the Mui prefix
// even though this app remaps component classes to Trinity.)
const NAV_ITEM_SX = {
  px: 2.5,
  '&:not(.Mui-selected)': { borderLeft: '5px solid transparent' },
} as const;

const Sidebar: React.FC<SidebarProps> = ({
  brand,
  selectedScenarioIds,
  onToggleScenario,
  onBrandPortfolio,
  destination,
  onNavigate,
  whatIfEnabled,
  tornadoEnabled,
  scenarioMode,
  tornadoPair,
  onTornadoPairChange,
  tornadoUpdateArmed = false,
  onTornadoUpdate,
}) => {
  const theme = useTheme();
  const isSingleSelect = scenarioMode === 'single';
  const isPairSelect = scenarioMode === 'pair';
  const atCap = !isSingleSelect && selectedScenarioIds.length >= MAX_SCENARIOS;
  const selectedTint = alpha(theme.palette.secondary.main, 0.12);
  // Single mode displays only the first stored selection as checked
  // (design doc D1) — the parent's full multi-selection stays intact
  // underneath, untouched by this display-only narrowing.
  const displayedSelectedIds = isSingleSelect
    ? selectedScenarioIds.slice(0, 1)
    : selectedScenarioIds;

  return (
    <Box
      sx={{
        width: EXEC_SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <List sx={{ pt: 1.5 }}>
        <ListItemButton
          selected={brand === null}
          onClick={onBrandPortfolio}
          sx={NAV_ITEM_SX}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <GridViewOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Brand Portfolio"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>
        <ListItemButton
          selected={brand !== null && destination === 'query'}
          disabled={brand === null}
          onClick={() => onNavigate('query')}
          sx={NAV_ITEM_SX}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <QueryStatsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Query Forecast"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>
        <ListItemButton
          selected={brand !== null && destination === 'whatif'}
          disabled={brand === null || !whatIfEnabled}
          onClick={() => onNavigate('whatif')}
          sx={NAV_ITEM_SX}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <InsightsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="What-If Analysis"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>
        <ListItemButton
          selected={brand !== null && destination === 'tornado'}
          disabled={brand === null || !tornadoEnabled}
          onClick={() => onNavigate('tornado')}
          sx={NAV_ITEM_SX}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AlignHorizontalLeftOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Scenario Drivers"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </ListItemButton>
      </List>

      <Divider sx={{ mx: 2.5, my: 1 }} />

      {brand === null ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2.5, py: 1 }}
        >
          Select a brand to get started.
        </Typography>
      ) : (
        <>
          <Box sx={{ px: 2.5, pt: 1, pb: 1.5 }}>
            <Typography variant="h6">{brand.model_name}</Typography>
            {brand.subheader && (
              <Typography variant="body2" color="text.secondary">
                {brand.subheader}
              </Typography>
            )}
          </Box>

          <Box sx={{ px: 2.5, pb: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontWeight: 600,
              }}
            >
              Scenarios
              {isSingleSelect && (
                <Box
                  component="span"
                  sx={{ textTransform: 'none', fontWeight: 400, ml: 0.5 }}
                >
                  (one at a time here)
                </Box>
              )}
            </Typography>
          </Box>
          {isPairSelect ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                px: 2.5,
                pt: 0.5,
                pb: 2,
              }}
            >
              <FormControl size="medium" fullWidth>
                <InputLabel id="exec-tornado-baseline-label">
                  Baseline
                </InputLabel>
                <Select
                  labelId="exec-tornado-baseline-label"
                  label="Baseline"
                  value={tornadoPair?.aId ?? ''}
                  onChange={(e) =>
                    tornadoPair &&
                    onTornadoPairChange?.({
                      aId: e.target.value as number,
                      bId: tornadoPair.bId,
                    })
                  }
                >
                  {brand.scenarios.map((scenario) => (
                    <MenuItem
                      key={scenario.scenario_id}
                      value={scenario.scenario_id}
                      disabled={scenario.scenario_id === tornadoPair?.bId}
                    >
                      {scenario.name}
                      {scenario.tag ? ` · ${scenario.tag}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Swapping is a draft edit like any dropdown change: it arms
                  the Update button, never auto-runs (a swap flips every
                  bar's sign, so it is a full re-run). Negative margins keep
                  the button from stretching the stack's gap. */}
              <Box sx={{ display: 'flex', justifyContent: 'center', my: -1 }}>
                <Tooltip title="Swap baseline and comparison" placement="right">
                  <IconButton
                    size="small"
                    aria-label="Swap baseline and comparison scenarios"
                    disabled={!tornadoPair}
                    onClick={() =>
                      tornadoPair &&
                      onTornadoPairChange?.({
                        aId: tornadoPair.bId,
                        bId: tornadoPair.aId,
                      })
                    }
                  >
                    <SwapVertOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <FormControl size="medium" fullWidth>
                <InputLabel id="exec-tornado-compare-label">
                  Compare to
                </InputLabel>
                <Select
                  labelId="exec-tornado-compare-label"
                  label="Compare to"
                  value={tornadoPair?.bId ?? ''}
                  onChange={(e) =>
                    tornadoPair &&
                    onTornadoPairChange?.({
                      aId: tornadoPair.aId,
                      bId: e.target.value as number,
                    })
                  }
                >
                  {brand.scenarios.map((scenario) => (
                    <MenuItem
                      key={scenario.scenario_id}
                      value={scenario.scenario_id}
                      disabled={scenario.scenario_id === tornadoPair?.aId}
                    >
                      {scenario.name}
                      {scenario.tag ? ` · ${scenario.tag}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {tornadoUpdateArmed && (
                <Button variant="contained" onClick={onTornadoUpdate}>
                  Update analysis
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 2.5,
                pb: 2,
              }}
            >
              {brand.scenarios.map((scenario) => {
                const selected = displayedSelectedIds.includes(
                  scenario.scenario_id
                );
                // Radio semantics in single mode: no min/cap tooltips, no
                // disabled rows — every chip is always clickable and a
                // click always replaces the (displayed) selection.
                const lastSelected =
                  !isSingleSelect &&
                  selected &&
                  selectedScenarioIds.length === 1;
                const capBlocked = !isSingleSelect && !selected && atCap;
                const chip = (
                  <Chip
                    key={scenario.scenario_id}
                    clickable
                    disabled={capBlocked}
                    onClick={() => onToggleScenario(scenario.scenario_id)}
                    label={
                      <>
                        {scenario.name}
                        {scenario.tag && (
                          <Box
                            component="span"
                            sx={{ color: 'text.secondary', ml: 0.5 }}
                          >
                            · {scenario.tag}
                          </Box>
                        )}
                      </>
                    }
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      height: 'auto',
                      minHeight: 32,
                      // Standardized to the meta-text tier (2026-08-16 font
                      // audit) instead of a one-off 14px.
                      fontSize: theme.typography.body2.fontSize,
                      border: '1px solid',
                      // Long scenario names wrap instead of ellipsizing —
                      // executives should never lose the tag suffix.
                      [`& .${chipClasses.label}`]: {
                        whiteSpace: 'normal',
                        py: 0.5,
                      },
                      ...(selected
                        ? {
                            bgcolor: selectedTint,
                            borderColor: 'secondary.main',
                            color: 'secondary.dark',
                            '&:hover': {
                              bgcolor: alpha(
                                theme.palette.secondary.main,
                                0.18
                              ),
                            },
                          }
                        : {
                            bgcolor: 'background.paper',
                            borderColor: 'divider',
                            color: 'text.primary',
                          }),
                    }}
                  />
                );
                if (lastSelected) {
                  return (
                    <Tooltip
                      key={scenario.scenario_id}
                      title="At least one scenario must stay selected"
                      placement="right"
                    >
                      <span style={{ display: 'flex' }}>{chip}</span>
                    </Tooltip>
                  );
                }
                if (capBlocked) {
                  return (
                    <Tooltip
                      key={scenario.scenario_id}
                      title={`Up to ${MAX_SCENARIOS} scenarios can be selected at once`}
                      placement="right"
                    >
                      <span style={{ display: 'flex' }}>{chip}</span>
                    </Tooltip>
                  );
                }
                return chip;
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Sidebar;
